import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import './TermsAndConditions.css';

const TermsAndConditions = ({ embedded = false }) => {
  const navigate = useNavigate();

  const content = (
    <>
        <h1>Terms &amp; Conditions</h1>
        <p className="legal-updated">Last updated: 30 September 2026</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Welcome to 1-19 Golf. These Terms &amp; Conditions ("Terms") govern your use of the
            1-19 Golf application and website located at{' '}
            <a href="https://1to19.com" target="_blank" rel="noopener noreferrer">https://1to19.com</a>{' '}
            (the "Service"), operated by One to Nineteen ("we", "us", or "our").
          </p>
          <p>
            By creating an account or using the Service, you agree to be bound by these Terms.
            If you do not agree, please do not use the Service.
          </p>
        </section>

        <section>
          <h2>2. Account Registration</h2>
          <p>
            To use certain features of the Service, you must create an account by providing your
            name, email address, and a password. You are responsible for:
          </p>
          <ul>
            <li>Providing accurate and complete information during registration.</li>
            <li>Maintaining the security of your account credentials.</li>
            <li>All activity that occurs under your account.</li>
          </ul>
          <p>
            You must be at least 16 years old to create an account. If we discover that an account
            has been created by someone under this age, we will terminate it.
          </p>
        </section>

        <section>
          <h2>3. Use of the Service</h2>
          <p>
            1-19 Golf is a golf society management platform. The Service allows you to:
          </p>
          <ul>
            <li>Create and join golf societies using invite codes.</li>
            <li>Record golf scores, handicaps, and tournament results.</li>
            <li>View leaderboards, manage tee sheets, and organise tournaments.</li>
            <li>Belong to multiple societies simultaneously.</li>
          </ul>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose.</li>
            <li>Attempt to gain unauthorised access to any part of the Service.</li>
            <li>Interfere with or disrupt the Service or its infrastructure.</li>
            <li>Upload or transmit any malicious code or content.</li>
            <li>Impersonate another person or misrepresent your identity.</li>
          </ul>
        </section>

        <section>
          <h2>4. Subscriptions and Payment</h2>
          <p>
            1-19 Golf is free to download. Full access to the Service requires a paid
            subscription ("Pro"), offered as an annual, automatically renewing subscription.
          </p>
          <p>
            The price is shown in the app before you purchase and in your store account. Prices
            vary by country and may change; where a change would affect a renewal, we will tell
            you beforehand. Where a free trial is offered, its length is shown before you
            subscribe, and any unused part of a trial ends when a paid subscription begins.
          </p>
          <p>
            Payment is taken by Apple or Google, not by us, and is charged to your App Store or
            Google Play account when you confirm the purchase.
          </p>
          <p>
            <strong>Your subscription renews automatically</strong> at the then-current price
            unless you cancel it at least 24 hours before the end of the current period. Your
            account is charged for the renewal within 24 hours of the end of that period.
          </p>
          <p>You can manage or cancel your subscription in your store account at any time:</p>
          <ul>
            <li><strong>iPhone:</strong> Settings &rarr; your name &rarr; Subscriptions.</li>
            <li><strong>Android:</strong> Play Store &rarr; your profile &rarr; Payments and subscriptions &rarr; Subscriptions.</li>
          </ul>
          <p>
            Cancelling stops the next renewal. You keep access until the end of the period you
            have already paid for. Deleting your account does <strong>not</strong> cancel a
            subscription &mdash; cancel it in your store account first.
          </p>
          <p>
            Refunds are handled by Apple or Google under their own policies, not by us. We may
            introduce further paid features or tiers in future; if we do, you will be told
            clearly before any charge applies and will not be billed without your consent.
          </p>
        </section>

        <section>
          <h2>5. User Content</h2>
          <p>
            You retain ownership of any data you submit to the Service (such as scores, player
            names, and tournament information). By using the Service, you grant us a licence to
            store, process, and display this data as necessary to operate the Service.
          </p>
          <p>
            You are responsible for ensuring that any content you submit does not infringe the
            rights of any third party.
          </p>
        </section>

        <section>
          <h2>6. Intellectual Property</h2>
          <p>
            The Service, including its design, logos, code, and documentation, is owned by
            One to Nineteen and is protected by intellectual property laws. You may not copy,
            modify, distribute, or reverse-engineer any part of the Service without our prior
            written consent.
          </p>
        </section>

        <section>
          <h2>7. Limitation of Liability</h2>
          <p>
            The Service is provided on an "as is" and "as available" basis. To the fullest extent
            permitted by law:
          </p>
          <ul>
            <li>
              We make no warranties, express or implied, regarding the Service's reliability,
              accuracy, or fitness for a particular purpose.
            </li>
            <li>
              We shall not be liable for any indirect, incidental, or consequential damages
              arising from your use of the Service.
            </li>
            <li>
              Our total liability to you for any claim shall not exceed the amount you have paid
              us (if any) in the 12 months preceding the claim.
            </li>
          </ul>
          <p>
            Nothing in these Terms excludes or limits our liability for death or personal injury
            caused by negligence, fraud, or any other liability that cannot be excluded by law.
          </p>
        </section>

        <section>
          <h2>8. Termination</h2>
          <p>
            You can delete your account at any time from <strong>My Profile</strong> in the app.
            If you no longer have the app installed, email us and we will delete it for you. We
            remove your personal data within 30 days, as described in our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            Deleting your account does not cancel a subscription billed by Apple or Google;
            cancel that in your store account first.
          </p>
          <p>
            We may suspend or terminate your access to the Service if you breach these Terms or
            if we reasonably believe your conduct is harmful to the Service or other users.
          </p>
          <p>
            Upon termination, your right to use the Service ceases immediately. We may retain
            certain data as required by law or for legitimate business purposes, as described in
            our <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
          </p>
        </section>

        <section>
          <h2>9. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make significant changes, we will
            notify you via the Service or by email. Your continued use of the Service after such
            changes constitutes acceptance of the updated Terms.
          </p>
        </section>

        <section>
          <h2>10. Governing Law</h2>
          <p>
            These Terms are governed by and construed in accordance with the laws of England and
            Wales. Any disputes arising from these Terms or your use of the Service shall be
            subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>
        </section>

        <section>
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="legal-contact">
            One to Nineteen<br />
            Email: <a href="mailto:1to19golf@gmail.com">1to19golf@gmail.com</a><br />
            Website: <a href="https://1to19.com" target="_blank" rel="noopener noreferrer">https://1to19.com</a>
          </p>
        </section>
    </>
  );

  if (embedded) return content;

  return (
    <div className="legal-page">
      <div className="legal-container">
        <button className="legal-back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={18} />
          <span>Back</span>
        </button>
        {content}
      </div>
    </div>
  );
};

export default TermsAndConditions;
